import React, { useEffect } from 'react';
import { User } from '../types/auth';
import { useMarkets } from '../hooks/useMarkets';
import AdminSettlementPanel from '../components/AdminSettlementPanel';
import OracleConsensusTable from '../components/OracleConsensusTable';
import MarketCycleStatus from '../components/MarketCycleStatus';
import TxToast from '../components/common/TxToast';

interface AdminPageProps {
  user: User | null;
}

/**
 * AdminPage
 *
 * Combined admin dashboard:
 * - Market list with settlement actions
 * - Oracle consensus view per market
 * - Aggregate stats
 */
export default function AdminPage({ user }: AdminPageProps) {
  const {
    markets,
    filteredMarkets,
    loadingMarkets,
    filter,
    setFilter,
    refetch,
    tx,
    resetTx,
  } = useMarkets();

  useEffect(() => {
    refetch();
  }, [refetch]);

  if (!user?.isAdmin) {
    return (
      <div className="page-container">
        <div className="access-denied">
          <span className="access-denied-icon">🔒</span>
          <h2>Admin Access Required</h2>
          <p>Only administrators can access this page.</p>
        </div>
      </div>
    );
  }

  const pendingCount  = markets.filter(m => !m.resolved).length;
  const resolvedCount = markets.filter(m =>  m.resolved).length;
  const expiredCount  = markets.filter(m => !m.resolved && m.deadline > 0 && Date.now() / 1000 > m.deadline).length;

  return (
    <div className="page-container">
      <div className="page-header">
        <h2 className="page-title">🛡️ Admin Dashboard</h2>
        <p className="page-subtitle">
          Manage market settlements, review oracle consensus, and monitor market lifecycle.
        </p>
      </div>

      {/* Stats bar */}
      <div className="settlement-status-bar">
        <span className="settlement-status-item">⏳ Pending: <strong>{pendingCount}</strong></span>
        <span className="settlement-status-item">✅ Resolved: <strong>{resolvedCount}</strong></span>
        <span className="settlement-status-item">🔴 Expired: <strong>{expiredCount}</strong></span>
        <button
          className="btn-outline"
          onClick={refetch}
          disabled={loadingMarkets}
          style={{ marginLeft: 'auto' }}
        >
          {loadingMarkets ? '⟳ Loading…' : '⟳ Refresh'}
        </button>
      </div>

      <TxToast tx={tx} onDismiss={resetTx} />

      {/* Filter tabs */}
      <div className="filter-tabs">
        {(['all', 'active', 'expired', 'resolved'] as const).map(f => (
          <button
            key={f}
            className={`filter-tab${filter === f ? ' active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Markets */}
      {loadingMarkets ? (
        <div className="loading-state">Loading markets…</div>
      ) : filteredMarkets.length === 0 ? (
        <div className="empty-state">No markets found for this filter.</div>
      ) : (
        <div className="admin-market-list">
          {filteredMarkets.map(market => (
            <div key={market.id} className="admin-market-card">
              <div className="admin-market-header">
                <span className="market-id">#{market.id}</span>
                <MarketCycleStatus market={market} compact />
              </div>

              <p className="market-question">{market.question}</p>

              <div className="admin-market-pools">
                <span>YES: {parseFloat(market.yesPool).toFixed(4)} ETH</span>
                <span>NO: {parseFloat(market.noPool).toFixed(4)} ETH</span>
              </div>

              {/* Settlement panel (only for unresolved markets) */}
              {!market.resolved && (
                <AdminSettlementPanel market={market} onSettled={refetch} />
              )}

              {/* Oracle consensus */}
              <details className="oracle-consensus-details">
                <summary className="oracle-consensus-summary">Oracle Consensus</summary>
                <OracleConsensusTable marketId={market.id} />
              </details>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
